import React, { ReactNode, useState } from 'react'
import * as RadixTooltip from '@radix-ui/react-tooltip'
import { useNavigate, useLocation } from 'react-router-dom'

import { Box } from 'components/layout/box'
import * as styles from './UniversalTooltip.css'
import { Text } from 'components/typography/text'
import { BlockIcon } from 'components/icons/block-icon'
import { NewsIcon, NavigatorIcon, HerdPowerIcon } from 'assets/icons/icons'

interface UniversalTooltipProps {
	children: ReactNode
	side?: 'top' | 'right' | 'bottom' | 'left'
	align?: 'start' | 'center' | 'end'
	ticker?: string
}

export const UniversalTooltip = ({ children, side = 'top', align = 'start', ticker }: UniversalTooltipProps) => {
	const [open, setOpen] = useState(false)

	// Handle touch start to open the tooltip
	const handleTouchStart = (e: React.TouchEvent) => {
		e.preventDefault()
		setOpen(true)
	}

	// Handle touch end to prevent default behavior but keep tooltip open
	// This prevents the tooltip from closing immediately on touch end
	const handleTouchEnd = (e: React.TouchEvent) => {
		e.preventDefault()
	}

	return (
		<RadixTooltip.Provider delayDuration={0} skipDelayDuration={0}>
			<RadixTooltip.Root open={open} onOpenChange={setOpen}>
				<RadixTooltip.Trigger asChild onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
					{children}
				</RadixTooltip.Trigger>
				<RadixTooltip.Portal>
					<RadixTooltip.Content
						side={side}
						align={align}
						sideOffset={5}
						style={{ zIndex: 1 }}
						onPointerDownOutside={() => setOpen(false)}>
						<Content ticker={ticker} />
						<RadixTooltip.Arrow className={styles.tooltipArrow} />
					</RadixTooltip.Content>
				</RadixTooltip.Portal>
			</RadixTooltip.Root>
		</RadixTooltip.Provider>
	)
}

type NavItem = {
	label: string
	icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
	path: string
	isDisabled?: boolean
}

const NavBar = ({ ticker }: { ticker?: string }) => {
	const navigate = useNavigate()
	const location = useLocation()
	const currentPath = location.pathname

	const navItems: NavItem[] = [
		{
			label: 'Navigator',
			icon: NavigatorIcon,
			path: `/decoder/navigator/${ticker}/summary`,
			isDisabled: currentPath.includes('/decoder/navigator')
		},
		{
			label: 'HerdPower',
			icon: HerdPowerIcon,
			path: `/herd/herdpower/${ticker}`,
			isDisabled: currentPath.includes('/herd/herdpower')
		},
		{
			label: 'News',
			icon: NewsIcon,
			path: `/news/${ticker?.toLowerCase()}`
			// isDisabled: currentPath.includes('/news')
		}
	]

	const handleNavClick = (path: string) => {
		navigate(path)
	}

	return (
		<Box className={styles.navBar}>
			{navItems.map((item, index) => (
				<Box
					key={index}
					className={`${styles.navItem} ${item.isDisabled ? styles.disabledButton : ''}`}
					onClick={() => handleNavClick(item.path)}>
					<Box className={styles.navIcon}>
						<BlockIcon icon={item.icon} size="medium" color="primary.500" />
					</Box>
					<Text className={styles.navText}>{item.label}</Text>
				</Box>
			))}
		</Box>
	)
}

const Content = ({ ticker }: { ticker?: string }) => {
	return (
		<Box display="flex" gap={5} alignItems="center" className={styles.tooltipContent}>
			{ticker && (
				<Text variant="body2Bold" color="primary.500">
					${ticker}
				</Text>
			)}
			<NavBar ticker={ticker} />
		</Box>
	)
}
